import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Get from "../../../helpers/ajax/get";
import Post from "../../../helpers/ajax/post";
import Put from "../../../helpers/ajax/put";
import Delete from "../../../helpers/ajax/delete";
import ModelWrapper from "./ModelWrapper";
import useConfirm from "../../../hooks/useConfirm";

var inputClass = "w-full block mb-3 rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-3 py-2";
var formButtonClass = "bg-black mt-5 block text-white w-full p-3 font-bold rounded-md focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 disabled:opacity-50";
var ghostButtonClass = "text-dependable hover:underline text-sm font-semibold";

// Manage all work-ticket dropdowns + submitter (name/email) values.
// Fields:
//   - "Who worked" (workers) - add via empty newline textarea, edit/delete individually
//   - "Types of Work" (workTypes) - add via empty newline textarea, edit/delete individually
//   - "Locations" (locations) - add via empty newline textarea, edit/delete individually
//   - "Submitter's Name" (submitters) - add one at a time (name + email); edit inline in place
const ManageDropdown = ({ close }) => {
    const { confirm } = useConfirm();
    const [data, setData] = useState({ dropdowns: [], submitters: [] });
    const [selectedKey, setSelectedKey] = useState("workers");
    const [showAddForm, setShowAddForm] = useState(false);
    const [newValuesText, setNewValuesText] = useState("");
    const [nameField, setNameField] = useState("");
    const [emailField, setEmailField] = useState("");
    const [editingIndex, setEditingIndex] = useState(null); // index of submitter being edited inline
    const [editName, setEditName] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editingValueIndex, setEditingValueIndex] = useState(null); // index of dropdown value being edited inline
    const [editValueText, setEditValueText] = useState("");
    const [hasError, setHasError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await Get("/api/manage-dropdowns");
                setData(res);
            } catch (error) {
                setHasError(error.message || error.error || "Failed to load dropdown values");
            }
        };
        load();
        // eslint-disable-next-line
    }, [reloadKey]);

    const selectedDropdown = data.dropdowns.find((d) => d.key === selectedKey) || { label: "", values: [] };

    // Add multiple newline-separated values (append only)
    const addValues = async (ev) => {
        ev.preventDefault();
        setBusy(true);
        setHasError(null);
        try {
            const values = newValuesText.split("\n").map((v) => v.trim()).filter(Boolean);
            if (!values.length) throw new Error("Enter at least one value");
            await Post(`/api/manage-dropdowns/values/bulk`, { key: selectedKey, values });
            toast("Values added");
            setNewValuesText("");
            setShowAddForm(false);
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        } finally {
            setBusy(false);
        }
    };

    // Add a new submitter (name + email) - the upper form is strictly for adding
    const addSubmitter = async (ev) => {
        ev.preventDefault();
        setBusy(true);
        setHasError(null);
        try {
            await Post(`/api/manage-dropdowns/submitters`, { name: nameField, email: emailField });
            toast("Submitter added");
            setNameField("");
            setEmailField("");
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        } finally {
            setBusy(false);
        }
    };

    // Save an inline edit of an existing submitter (in place)
    const saveSubmitterEdit = async (ev) => {
        ev.preventDefault();
        setBusy(true);
        setHasError(null);
        try {
            const sub = data.submitters[editingIndex];
            await Put(`/api/manage-dropdowns/submitters`, {
                oldName: sub.name,
                name: editName,
                email: editEmail,
            });
            toast("Submitter updated");
            setEditingIndex(null);
            setEditName("");
            setEditEmail("");
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        } finally {
            setBusy(false);
        }
    };

    const deleteSubmitter = async (sub) => {
        const ok = await confirm({
            title: "Delete Submitter",
            text: `Are you sure you want to delete submitter <strong>"${sub.name}"</strong>?`,
            confirmText: "Delete",
        });
        if (!ok) return;
        try {
            await Delete(`/api/manage-dropdowns/submitters/${encodeURIComponent(sub.name)}`);
            toast("Submitter deleted");
            if (editingIndex !== null && data.submitters[editingIndex]?.name === sub.name) {
                setEditingIndex(null);
                setEditName("");
                setEditEmail("");
            }
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        }
    };

    // Save an inline edit of an existing dropdown value (in place)
    const saveValueEdit = async (ev) => {
        ev.preventDefault();
        setBusy(true);
        setHasError(null);
        try {
            const oldValue = selectedDropdown.values[editingValueIndex];
            await Put(`/api/manage-dropdowns/values/rename`, {
                key: selectedKey,
                oldValue,
                newValue: editValueText.trim(),
            });
            toast("Value updated");
            setEditingValueIndex(null);
            setEditValueText("");
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        } finally {
            setBusy(false);
        }
    };

    const removeValue = async (value) => {
        const ok = await confirm({
            title: "Delete Value",
            text: `Are you sure you want to delete value <strong>"${value}"</strong>?`,
            confirmText: "Delete",
        });
        if (!ok) return;
        try {
            await Delete(`/api/manage-dropdowns/values`, { key: selectedKey, value });
            toast("Value deleted");
            setReloadKey((k) => k + 1);
        } catch (error) {
            setHasError(error.message || error.error || "An error occurred");
        }
    };

    const isSubmitter = selectedKey === "submitters";

    return (
        <ModelWrapper width={"w-[40rem]"} close={close} title={"Manage Dropdown Values"}>
            <select
                className={inputClass}
                value={isSubmitter ? "submitters" : selectedKey}
                onChange={(e) => {
                    setSelectedKey(e.target.value);
                    setShowAddForm(false);
                    setNewValuesText("");
                    setNameField("");
                    setEmailField("");
                    setEditingIndex(null);
                    setEditName("");
                    setEditEmail("");
                    setEditingValueIndex(null);
                    setEditValueText("");
                }}
            >
                {[{ key: "submitters", label: "Submitter's Name" }, ...data.dropdowns].map((d) => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                ))}
            </select>

            {hasError && <p className="text-red-800 text-left mt-4">{hasError}</p>}

            {isSubmitter ? (
                <div className="pt-4">
                    <p className="font-semibold mb-2 text-gray-700">Add a submitter:</p>
                    <div className="grid grid-cols-2 gap-3">
                        <input value={nameField} onChange={(e) => setNameField(e.target.value)} placeholder="Full name" type="text" className={inputClass} />
                        <input value={emailField} onChange={(e) => setEmailField(e.target.value)} placeholder="Email address" type="email" className={inputClass} />
                    </div>
                    <button className={formButtonClass} onClick={addSubmitter} disabled={busy}>
                        Add Submitter
                    </button>

                    <hr className="my-5 border-t" />
                    <p className="font-semibold mb-2 text-gray-700">Existing submitters (edit or delete):</p>
                    <div className="max-h-72 overflow-y-auto">
                        {data.submitters.map((sub, i) => (
                            editingIndex === i ? (
                                <form key={sub.name} onSubmit={saveSubmitterEdit} className="p-2 border-b border-gray-200">
                                    <div className="grid grid-cols-2 gap-3">
                                        <input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Full name" type="text" className={inputClass} />
                                        <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="Email address" type="email" className={inputClass} />
                                    </div>
                                    <div className="flex gap-2 mt-2">
                                        <button type="submit" className="bg-black text-white px-3 py-1 rounded-md text-sm font-semibold" disabled={busy}>Save</button>
                                        <button type="button" className="border border-gray-300 px-3 py-1 rounded-md text-sm font-semibold hover:bg-gray-50" onClick={() => {
                                            setEditingIndex(null);
                                            setEditName("");
                                            setEditEmail("");
                                        }}>Cancel</button>
                                    </div>
                                </form>
                            ) : (
                                <div key={sub.name} className="flex items-center justify-between p-2 border-b border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <span>{sub.name}</span>
                                        <span className="text-xs text-gray-500">{sub.email}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button className="text-dependable hover:underline text-sm" onClick={() => {
                                            setEditingIndex(i);
                                            setEditName(sub.name);
                                            setEditEmail(sub.email);
                                        }}>Edit</button>
                                        <button className="text-red-600 hover:underline text-sm" onClick={() => deleteSubmitter(sub)}>Delete</button>
                                    </div>
                                </div>
                            )
                        ))}
                        {data.submitters.length === 0 && <p className="text-sm text-gray-400">No submitters yet.</p>}
                    </div>
                </div>
            ) : (
                <div className="pt-4">
                    {!showAddForm ? (
                        <button
                            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:opacity-90"
                            onClick={() => setShowAddForm(true)}
                        >
                            + Add Values
                        </button>
                    ) : (
                        <form onSubmit={addValues}>
                            <label className="block text-sm font-semibold mb-2 text-gray-700">
                                Add new values, one per line. These will be added to the existing list.
                            </label>
                            <textarea
                                value={newValuesText}
                                onChange={(e) => setNewValuesText(e.target.value)}
                                rows={6}
                                autoFocus
                                className="w-full block rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-3 py-2"
                                placeholder={"Value one\nValue two\nValue three"}
                            />
                            <div className="flex gap-3 mt-3">
                                <button type="submit" className={formButtonClass} disabled={busy}>Add Values</button>
                                <button
                                    type="button"
                                    className="w-full border border-gray-300 mt-5 p-3 font-semibold rounded-md hover:bg-gray-50"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewValuesText("");
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}

                    <hr className="my-5 border-t" />
                    <p className="font-semibold mb-2 text-gray-700">Edit or delete individual values:</p>
                    <div className="max-h-72 overflow-y-auto">
                        {selectedDropdown.values.map((val, i) => (
                            editingValueIndex === i ? (
                                <form key={val} onSubmit={saveValueEdit} className="p-2 border-b border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <input
                                            value={editValueText}
                                            onChange={(e) => setEditValueText(e.target.value)}
                                            type="text"
                                            autoFocus
                                            className="w-full block rounded-md border focus:outline-none focus:ring-2 ring-offset-2 ring-black/30 px-3 py-1"
                                        />
                                        <button type="submit" className="bg-black text-white px-3 py-1 rounded-md text-sm font-semibold whitespace-nowrap" disabled={busy}>Save</button>
                                        <button type="button" className="border border-gray-300 px-3 py-1 rounded-md text-sm font-semibold whitespace-nowrap hover:bg-gray-50" onClick={() => {
                                            setEditingValueIndex(null);
                                            setEditValueText("");
                                        }}>Cancel</button>
                                    </div>
                                </form>
                            ) : (
                                <div key={val} className="flex items-center justify-between p-2 border-b border-gray-200">
                                    <span>{val}</span>
                                    <div className="flex gap-2">
                                        <button className={ghostButtonClass}
                                            onClick={() => {
                                                setEditingValueIndex(i);
                                                setEditValueText(val);
                                            }}>
                                            Edit
                                        </button>
                                        <button className="text-red-600 hover:underline text-sm" onClick={() => removeValue(val)}>Delete</button>
                                    </div>
                                </div>
                            )
                        ))}
                        {selectedDropdown.values.length === 0 && <p className="text-sm text-gray-400">No values yet.</p>}
                    </div>
                </div>
            )}
        </ModelWrapper>
    );
};

export default ManageDropdown;